import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";

export default function TermsOfUse() {
  return (
    <div className="min-h-screen flex flex-col">
      <MarketingHeader />
      <div className="flex-1 container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold mb-8 tracking-wide">Terms of Use</h1>
        
        <div className="prose prose-slate dark:prose-invert max-w-none">
          <p className="text-muted-foreground mb-6">
            <strong>Last Updated:</strong> June 29, 2026
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground mb-4">
              By accessing or using Fill The Trucks ("the Platform"), you agree to be bound by these Terms of Use ("Terms"). If you do not agree to these Terms, you may not use the Platform. Fill The Trucks reserves the right to modify these Terms at any time, and your continued use constitutes acceptance of any changes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">2. Platform Description</h2>
            <p className="text-muted-foreground mb-4">
              Fill The Trucks is a freight marketplace platform that connects Shippers, Carriers, and Brokers for LTL (Less Than Truckload) freight transportation. The Platform facilitates connections and communication between users but does not directly provide transportation services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">3. User Accounts and Eligibility</h2>
            
            <h3 className="text-xl font-semibold mb-3">Account Creation</h3>
            <p className="text-muted-foreground mb-4">
              To use the Platform, you must create an account and select a user type (Shipper, Carrier, or Broker). You agree to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and update your account information</li>
              <li>Keep your login credentials confidential</li>
              <li>Be responsible for all activities under your account</li>
              <li>Notify us immediately of unauthorized access</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">Eligibility</h3>
            <p className="text-muted-foreground mb-4">
              You must be at least 18 years old and legally authorized to conduct business to use this Platform. By using the Platform, you represent that you meet these requirements.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">4. Membership and Payments</h2>
            
            <h3 className="text-xl font-semibold mb-3">Free Plan</h3>
            <p className="text-muted-foreground mb-4">
              The Free plan is available at no cost with no time limit. Free plan users can access limited features as described on our pricing page. Upgrading to a paid plan unlocks additional features.
            </p>

            <h3 className="text-xl font-semibold mb-3">Subscription Pricing</h3>
            <p className="text-muted-foreground mb-4">
              Pricing is based on your role:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li><strong>Shippers:</strong> Free (3 shipments/month) or $25/month for unlimited shipments</li>
              <li><strong>Carriers:</strong> Free (bid on invited loads) or $25/month for full board access (Search &amp; Bid)</li>
              <li><strong>Brokers:</strong> Free (bid on invited loads), $25/month for Search &amp; Bid, or $45/month for Search, Bid &amp; Post</li>
            </ul>
            <p className="text-muted-foreground mb-4">
              Payments are processed through Stripe, and subscriptions automatically renew unless cancelled.
            </p>

            <h3 className="text-xl font-semibold mb-3">Billing and Cancellation</h3>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li>You will be charged on the same day each month (monthly) or year (annual) as your initial subscription</li>
              <li>You may cancel your subscription at any time through your account settings</li>
              <li>Cancellations take effect at the end of the current billing period</li>
              <li>No refunds are provided for partial billing periods</li>
              <li>We reserve the right to change pricing with 30 days advance notice</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">5. Platform Usage and User Conduct</h2>
            
            <h3 className="text-xl font-semibold mb-3">Permitted Use</h3>
            <p className="text-muted-foreground mb-4">
              You may use the Platform to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li>Post freight loads (Shippers and Brokers)</li>
              <li>Post truck availability (Carriers and Brokers)</li>
              <li>Search for and connect with potential business partners</li>
              <li>Communicate and negotiate directly with other users</li>
              <li>Manage your transactions and account</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">Prohibited Conduct</h3>
            <p className="text-muted-foreground mb-4">
              You agree NOT to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li>Provide false, misleading, or fraudulent information</li>
              <li>Impersonate another person or entity</li>
              <li>Use the Platform for illegal purposes</li>
              <li>Harass, threaten, or abuse other users</li>
              <li>Post spam, advertisements, or irrelevant content</li>
              <li>Scrape, mine, or extract data from the Platform</li>
              <li>Circumvent security measures or access controls</li>
              <li>Interfere with the operation of the Platform</li>
              <li>Violate any applicable laws or regulations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">6. Transactions and Agreements</h2>
            
            <h3 className="text-xl font-semibold mb-3">Platform Role and Limitations</h3>
            <p className="text-muted-foreground mb-4">
              <strong>Fill The Trucks is a marketplace platform only.</strong> We provide a venue for Shippers, Carriers, and Brokers to connect and communicate. We do not participate in, control, or guarantee any transactions between users.
            </p>
            <p className="text-muted-foreground mb-4">
              <strong>By using this Platform, you explicitly acknowledge and agree that Fill The Trucks:</strong>
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li><strong>Does NOT arrange transportation.</strong> We do not coordinate, organize, or facilitate the actual movement of freight. All transportation arrangements are made directly between users.</li>
              <li><strong>Does NOT select, recommend, or endorse any carrier.</strong> We do not evaluate, vet, or recommend any carrier for any shipment. The selection of a carrier is solely the responsibility of the shipper or broker.</li>
              <li><strong>Does NOT negotiate rates or terms.</strong> We do not participate in rate negotiations, pricing discussions, or contract terms between users. All pricing and terms are determined exclusively by the parties involved.</li>
              <li><strong>Is NOT a party to any shipment contract.</strong> Any agreement, contract, or arrangement for transportation services is made directly between the shipper and carrier (or broker). Fill The Trucks is not a party to, and has no obligations under, any such agreement.</li>
              <li><strong>Has NO obligation upon quote acceptance.</strong> When a user accepts a quote on the Platform, this creates an agreement solely between the users involved. Fill The Trucks has no obligations, responsibilities, or liabilities arising from quote acceptance or any resulting transaction.</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">Disclaimer of Responsibility</h3>
            <p className="text-muted-foreground mb-4">
              <strong>Fill The Trucks expressly disclaims all responsibility for:</strong>
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li><strong>Payment:</strong> We are not responsible for ensuring payment between parties, collecting payments, or resolving payment disputes.</li>
              <li><strong>Carrier Performance:</strong> We are not responsible for the performance, conduct, or reliability of any carrier, including on-time delivery, service quality, or compliance with agreements.</li>
              <li><strong>Cargo Loss or Damage:</strong> We are not responsible for any loss, damage, theft, or destruction of cargo during transportation.</li>
              <li><strong>Disputes:</strong> We are not responsible for mediating, arbitrating, or resolving any disputes between users, including disputes over pricing, service quality, damage claims, or contractual obligations.</li>
              <li><strong>Insurance:</strong> We do not provide cargo insurance or any form of coverage. Users are solely responsible for obtaining appropriate insurance for their shipments.</li>
            </ul>
            <p className="text-muted-foreground mb-4">
              All contracts for transportation are directly between the shipper and carrier (or broker). Users are solely responsible for performing due diligence on potential business partners before entering into any agreement.
            </p>

            <h3 className="text-xl font-semibold mb-3">User Responsibility</h3>
            <p className="text-muted-foreground mb-4">
              Users are responsible for:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li>Verifying the legitimacy and credentials of other parties</li>
              <li>Negotiating and documenting all transaction terms</li>
              <li>Ensuring compliance with transportation regulations</li>
              <li>Obtaining appropriate insurance and licenses</li>
              <li>Resolving disputes directly with the other party</li>
              <li>Meeting all contractual obligations</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">No Platform Commission</h3>
            <p className="text-muted-foreground mb-4">
              Fill The Trucks does not take a commission or percentage of transaction values. Your subscription fee is the only charge from the Platform.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">7. Content and Intellectual Property</h2>
            
            <h3 className="text-xl font-semibold mb-3">User Content</h3>
            <p className="text-muted-foreground mb-4">
              You retain ownership of content you post on the Platform (load details, truck availability, messages, etc.). By posting content, you grant Fill The Trucks a non-exclusive, worldwide, royalty-free license to use, display, and distribute your content as necessary to operate the Platform.
            </p>

            <h3 className="text-xl font-semibold mb-3">Platform Content</h3>
            <p className="text-muted-foreground mb-4">
              All Platform content, features, and functionality (including but not limited to design, text, graphics, logos, and software) are owned by Fill The Trucks and protected by copyright, trademark, and other intellectual property laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">8. Disclaimers and Limitation of Liability</h2>
            
            <h3 className="text-xl font-semibold mb-3">Platform Provided "As Is"</h3>
            <p className="text-muted-foreground mb-4">
              THE PLATFORM IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. FILL THE TRUCKS DISCLAIMS ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>

            <h3 className="text-xl font-semibold mb-3">No Guarantee of Results</h3>
            <p className="text-muted-foreground mb-4">
              We do not guarantee that you will find suitable partners, complete transactions, or achieve any particular result from using the Platform.
            </p>

            <h3 className="text-xl font-semibold mb-3">Limitation of Liability</h3>
            <p className="text-muted-foreground mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, FILL THE TRUCKS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES RESULTING FROM:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li>Your use or inability to use the Platform</li>
              <li>Any conduct or content of other users</li>
              <li>Unauthorized access to your account or data</li>
              <li>Any errors, interruptions, or technical problems</li>
              <li>Failed transactions or business relationships</li>
            </ul>
            <p className="text-muted-foreground mb-4">
              IN NO EVENT SHALL FILL THE TRUCKS' TOTAL LIABILITY EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRIOR TO THE EVENT GIVING RISE TO LIABILITY.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">9. Indemnification</h2>
            <p className="text-muted-foreground mb-4">
              You agree to indemnify, defend, and hold harmless Fill The Trucks and its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses (including legal fees) arising from:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li>Your use of the Platform</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another party</li>
              <li>Your content or conduct on the Platform</li>
              <li>Any transactions or agreements with other users</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">10. Account Suspension and Termination</h2>
            <p className="text-muted-foreground mb-4">
              Fill The Trucks reserves the right to suspend or terminate your account at any time, with or without notice, for:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-4">
              <li>Violation of these Terms</li>
              <li>Fraudulent or illegal activity</li>
              <li>Abusive behavior toward other users or staff</li>
              <li>Non-payment of subscription fees</li>
              <li>Any other reason at our sole discretion</li>
            </ul>
            <p className="text-muted-foreground mb-4">
              You may terminate your account at any time through your account settings. Termination does not relieve you of any obligations incurred prior to termination.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">11. Dispute Resolution</h2>
            
            <h3 className="text-xl font-semibold mb-3">User Disputes</h3>
            <p className="text-muted-foreground mb-4">
              Fill The Trucks is not responsible for disputes between users. You agree to resolve all transaction disputes directly with the other party.
            </p>

            <h3 className="text-xl font-semibold mb-3">Disputes with Fill The Trucks</h3>
            <p className="text-muted-foreground mb-4">
              Any disputes arising from these Terms or your use of the Platform shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association, except that either party may seek injunctive relief in court to protect intellectual property rights.
            </p>

            <h3 className="text-xl font-semibold mb-3">Governing Law</h3>
            <p className="text-muted-foreground mb-4">
              These Terms shall be governed by and construed in accordance with the laws of the State of Georgia, without regard to conflict of law principles.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">12. Miscellaneous</h2>
            
            <h3 className="text-xl font-semibold mb-3">Entire Agreement</h3>
            <p className="text-muted-foreground mb-4">
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and Fill The Trucks regarding use of the Platform.
            </p>

            <h3 className="text-xl font-semibold mb-3">Severability</h3>
            <p className="text-muted-foreground mb-4">
              If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.
            </p>

            <h3 className="text-xl font-semibold mb-3">Waiver</h3>
            <p className="text-muted-foreground mb-4">
              Our failure to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision.
            </p>

            <h3 className="text-xl font-semibold mb-3">Assignment</h3>
            <p className="text-muted-foreground mb-4">
              You may not assign or transfer these Terms or your account without our prior written consent. We may assign these Terms at any time without notice.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">13. Contact Information</h2>
            <p className="text-muted-foreground mb-4">
              For questions about these Terms, please contact us:
            </p>
            <ul className="list-none text-muted-foreground mb-4">
              <li><strong>Email:</strong> hello@fillthetrucks.com</li>
            </ul>
          </section>

          <section className="mb-8">
            <p className="text-muted-foreground">
              By using Fill The Trucks, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use.
            </p>
          </section>
        </div>
      </div>
      <MarketingFooter />
    </div>
  );
}
